import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Search, Loader2, ChevronDown, ChevronUp, Users, BookOpen, Calendar, Briefcase, X, BarChart3, Info, ListChecks, ClipboardCheck, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ZohoCustomerLink, ZohoInvoiceLink } from '../../components/ZohoLinks';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import BatchProgressModal from './BatchProgressModal';
import TrainerGuideModal from './TrainerGuideModal';
import BatchTopicCompletionDialog from './BatchTopicCompletionDialog';

// ─── Searchable Select ───────────────────────────────────────
function SearchableSelect({ options, value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find(o => o.value === value);
  const filtered = query ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : options;

  return (
    <div className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={open ? query : (selected ? selected.label : '')}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          className="h-9 pl-8 text-xs w-full"
        />
        {value && (
          <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-400" onClick={() => { onChange(''); setQuery(''); }}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No results</div>
            ) : filtered.map(o => (
              <div
                key={o.value}
                className={`px-3 py-2 text-xs cursor-pointer hover:bg-violet-50 ${o.value === value ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-700'}`}
                onClick={() => { onChange(o.value); setQuery(''); setOpen(false); }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Custom Chart Tooltip ────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const weekday = payload.find(p => p.dataKey === 'weekday')?.value || 0;
  const weekend = payload.find(p => p.dataKey === 'weekend')?.value || 0;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 mb-1.5">{label}</p>
      <div className="flex items-center gap-2 mb-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#4361ee]" /> Weekday: <span className="font-bold">{weekday}</span></div>
      <div className="flex items-center gap-2 mb-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#e63946]" /> Weekend: <span className="font-bold">{weekend}</span></div>
      <div className="border-t pt-1 mt-1 font-semibold text-slate-700">Total: {weekday + weekend}</div>
    </div>
  );
}

// ─── Batch Popover ───────────────────────────────────────────
function BatchPopover({ batch }) {
  const b = batch;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-xs text-violet-600 hover:text-violet-800 hover:underline font-medium text-left" data-testid={`batch-popover-${b.batch_number}`}>
          {b.batch_name}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b bg-slate-50/70">
          <p className="text-sm font-semibold text-slate-800">{b.batch_name}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">#{b.batch_number} &middot; {b.batch_status}</p>
        </div>
        <div className="p-3 space-y-2 text-xs">
          <Row label="Course" value={b.course_name} />
          <Row label="Centre" value={b.centre_name} />
          <Row label="Batch Type" value={b.batch_type} />
          <Row label="Mode" value={b.training_mode} />
          <Row label="Timing" value={`${b.start_time || ''} - ${b.end_time || ''}`.trim().replace(/^-|-$/g, '') || '—'} />
          <Row label="Start Date" value={b.start_date || '—'} />
          <Row label="End Date" value={b.end_date || '—'} />
          <Row label="Students" value={b.student_count} />
          {(b.whatsapp_link_crm || b.audit_trainer_name || b.notes_of_auditor) && (
            <div className="border-t pt-2 space-y-1.5">
              <p className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-emerald-500" /> From Zoho CRM
              </p>
              {b.whatsapp_link_crm && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] text-slate-400 uppercase mt-0.5 shrink-0">WhatsApp</span>
                  <a href={b.whatsapp_link_crm} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] text-violet-600 hover:underline truncate max-w-[190px]"
                    data-testid={`bp-popover-wa-${b.batch_number}`}>Open link ↗</a>
                </div>
              )}
              {b.audit_trainer_name && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] text-slate-400 uppercase mt-0.5 shrink-0">Audit Trainer</span>
                  <span className="text-[11px] text-slate-700 text-right" data-testid={`bp-popover-audit-trainer-${b.batch_number}`}>{b.audit_trainer_name}</span>
                </div>
              )}
              {b.notes_of_auditor && (
                <div>
                  <span className="text-[10px] text-slate-400 uppercase">Notes of Auditor</span>
                  <p className="text-[11px] text-slate-700 mt-0.5 whitespace-pre-wrap leading-relaxed" data-testid={`bp-popover-audit-notes-${b.batch_number}`}>{b.notes_of_auditor}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PAYMENT_BADGE = {
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partially_paid: { label: 'Partially Paid', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  overdue: { label: 'Overdue', cls: 'bg-red-50 text-red-700 border-red-200' },
};

// ─── Student Detail Drilldown ────────────────────────────────
// ─── Attendance pill helpers ───────────────────────────────
// Used inside BatchSection: colour-coded compact badge for attendance %
function attendancePillClass(pct) {
  if (pct === null || pct === undefined) return 'bg-slate-100 text-slate-400 border-slate-200';
  if (pct >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (pct >= 50) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
}

function AttendanceCell({ data }) {
  // `data` is the per-student record from /batch-summary
  if (!data) {
    return <span className="text-[10px] text-slate-300">—</span>;
  }
  const { attendance_pct, sessions_attended, sessions_held, present, late, absent, last_attended_date } = data;
  const label = attendance_pct == null ? '—' : `${attendance_pct}%`;
  const tooltip =
    attendance_pct == null
      ? `No sessions marked yet for this student`
      : `${sessions_attended}/${sessions_held} sessions  ·  ${present} present · ${late} late · ${absent} absent${last_attended_date ? `  ·  Last: ${last_attended_date}` : ''}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold tabular-nums ${attendancePillClass(attendance_pct)}`}
      title={tooltip}
      data-testid="attendance-pill"
    >
      {label}
      {sessions_held > 0 && (
        <span className="text-[9px] font-normal opacity-70">· {sessions_attended}/{sessions_held}</span>
      )}
    </span>
  );
}

function StudentDrilldown({ student, attendance }) {
  const [expanded, setExpanded] = useState(false);
  const s = student;
  const pb = PAYMENT_BADGE[s.payment_status] || PAYMENT_BADGE.paid;
  const { map } = useBookmarkMap();
  const bmHighlight = bookmarkRowClass(s.enrollment_no, map);
  return (
    <>
      <TableRow className={`hover:bg-violet-50/30 cursor-pointer ${bmHighlight}`} onClick={() => setExpanded(!expanded)} data-testid={`student-row-${s.enrollment_no}`}>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-800">{s.name}</span>
            <BookmarkInline student={{ enrollment_no: s.enrollment_no, id: s.student_id || s.enrollment_no, name: s.name, course: s.course, due_amount: s.due_amount }} />
            <span className="text-[10px] text-slate-400 font-mono">
              <ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} />
            </span>
          </div>
        </TableCell>
        <TableCell><AttendanceCell data={attendance} /></TableCell>
        <TableCell><Badge variant="outline" className={`text-[9px] ${pb.cls}`}>{pb.label}</Badge></TableCell>
        <TableCell className="text-xs text-right font-medium text-slate-700">{s.total_amount ? `₹${Number(s.total_amount).toLocaleString('en-IN')}` : '—'}</TableCell>
        <TableCell className="text-xs text-right text-emerald-600 font-medium">{s.paid_amount ? `₹${Number(s.paid_amount).toLocaleString('en-IN')}` : '—'}</TableCell>
        <TableCell className={`text-xs text-right font-medium ${s.due_amount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{s.due_amount ? `₹${Number(s.due_amount).toLocaleString('en-IN')}` : '₹0'}</TableCell>
        <TableCell className="text-center">{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 inline" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 inline" />}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-slate-50/60 p-0">
            <div className="px-6 py-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
              <Detail label="Phone" value={s.phone || '—'} />
              <Detail label="Email" value={s.email || '—'} />
              <Detail label="Course" value={s.course || '—'} />
              <Detail label="Branch" value={s.branch || '—'} />
              <Detail label="Total Amount" value={s.total_amount ? `₹${Number(s.total_amount).toLocaleString('en-IN')}` : '—'} />
              <Detail label="Paid" value={s.paid_amount ? `₹${Number(s.paid_amount).toLocaleString('en-IN')}` : '—'} />
              <Detail label="Due" value={s.due_amount ? `₹${Number(s.due_amount).toLocaleString('en-IN')}` : '₹0'} highlight={s.due_amount > 0} />
              <Detail label="Salesperson" value={s.salesperson || '—'} />
              <Detail label="Enrollment Type" value={s.enrollment_type || 'regular'} />
              <Detail label="Status" value={s.enrollment_status || 'active'} />
            </div>
            {/* Invoice Links */}
            {s.invoices && s.invoices.length > 0 && (
              <div className="px-6 pb-3 border-t border-slate-100 pt-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Invoices</p>
                <div className="flex flex-wrap gap-2">
                  {s.invoices.map(inv => (
                    <div key={inv.invoice_id} className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded px-2.5 py-1" data-testid={`invoice-chip-${inv.invoice_id}`}>
                      <ZohoInvoiceLink invoiceNumber={inv.invoice_number} invoiceId={inv.invoice_id} className="text-[11px]" />
                      <Badge variant="outline" className={`text-[8px] px-1 py-0 ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : inv.status === 'overdue' ? 'bg-red-50 text-red-600 border-red-200' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'text-slate-500'}`}>
                        {inv.status}
                      </Badge>
                      <span className="text-[10px] text-slate-500">₹{Number(inv.total || 0).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-400">{label}</span>
      <span className="font-medium text-slate-700 text-right">{value || '—'}</span>
    </div>
  );
}

function Detail({ label, value, highlight }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-xs font-medium mt-0.5 ${highlight ? 'text-red-600' : 'text-slate-700'}`}>{value}</p>
    </div>
  );
}

// ─── Batch Section (merged batch row with students) ──────────
function BatchSection({ batch }) {
  const [expanded, setExpanded] = useState(false);
  const [attnByStudent, setAttnByStudent] = useState(null); // { student_id: {…} }
  const [attnSummary, setAttnSummary] = useState(null);     // { sessions_held, avg_attendance_pct }
  const [attnLoading, setAttnLoading] = useState(false);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const b = batch;

  // Lazy-fetch attendance the first time the batch is expanded.
  // Range = batch.start_date → batch.end_date (backend clamps end_date to
  // today for Running/Upcoming batches). Re-fetches automatically if the
  // batch's start_date / end_date change on next open (cache resets by
  // keying on those).
  useEffect(() => {
    if (!expanded) return;
    if (attnByStudent) return; // already have it
    if (!b.batch_id) return;
    setAttnLoading(true);
    api.get(`/training/daily-attendance/batch-summary/${b.batch_id}`)
      .then(r => {
        const map = {};
        for (const row of (r.data?.students || [])) {
          map[row.student_id] = row;
        }
        setAttnByStudent(map);
        setAttnSummary({
          sessions_held: r.data?.sessions_held ?? 0,
          avg_attendance_pct: r.data?.avg_attendance_pct ?? null,
        });
      })
      .catch(() => {
        // Silent — attendance is a nice-to-have; empty map means "—" pills
        setAttnByStudent({});
      })
      .finally(() => setAttnLoading(false));
  }, [expanded, b.batch_id, b.start_date, b.end_date, attnByStudent]);

  return (
    <div className="border-b last:border-b-0" data-testid={`batch-section-${b.batch_number}`}>
      <div
        className="flex items-center justify-between px-5 py-2.5 bg-white hover:bg-violet-50/30 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-violet-700 font-mono">#{b.batch_number}</span>
          <BatchPopover batch={b} />
          <span className="text-[10px] text-slate-400">{b.course_name}</span>
          <Badge variant="outline" className="text-[9px]">{b.student_count} students</Badge>
          <Badge variant="outline" className={`text-[9px] ${b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{b.batch_status}</Badge>
          {(b.audit_trainer_name || b.notes_of_auditor) && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 max-w-[220px]"
              title={`Audit Trainer: ${b.audit_trainer_name || '—'}${b.notes_of_auditor ? `\nNotes: ${b.notes_of_auditor}` : ''}`}
              data-testid={`batch-audit-chip-${b.batch_number}`}
            >
              <ClipboardCheck className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {b.audit_trainer_name ? `Audit · ${b.audit_trainer_name}` : 'Auditor Notes'}
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-400">{b.start_date} {b.start_time} — {b.end_date || '—'}</span>
          {b.topic_completion_pct != null && (
            <span className="inline-flex items-center gap-1">
              <span className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${b.topic_completion_pct >= 50 ? 'bg-emerald-50 text-emerald-700' : b.topic_completion_pct > 0 ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-500'}`} title="Topic completion %" data-testid={`batch-completion-${b.batch_number}`}>
                {b.topic_completion_pct}% done
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setTopicsOpen(true); }}
                className="inline-flex items-center justify-center w-5 h-5 rounded border border-violet-200 bg-violet-50 text-violet-600 hover:bg-violet-100 hover:text-violet-800 transition"
                title="View topic completion"
                data-testid={`batch-view-topics-${b.batch_number}`}
              >
                <BookOpen className="w-2.5 h-2.5" />
              </button>
            </span>
          )}
          <span className="text-xs font-semibold text-slate-700">₹{Number(b.batch_revenue || 0).toLocaleString('en-IN')}</span>
          {b.batch_due > 0 && <span className="text-[10px] text-red-500 font-medium">Due: ₹{Number(b.batch_due).toLocaleString('en-IN')}</span>}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </div>
      {topicsOpen && <BatchTopicCompletionDialog batchId={b.batch_id} onClose={() => setTopicsOpen(false)} />}
      {expanded && (
        <div className="bg-slate-50/30">
          {(b.audit_trainer_name || b.notes_of_auditor) && (
            <div className="px-5 py-2 text-[11px] bg-emerald-50/60 border-b border-emerald-100 flex items-start gap-3" data-testid={`batch-audit-banner-${b.batch_number}`}>
              <span className="inline-flex items-center gap-1 shrink-0 text-[9px] uppercase font-semibold text-emerald-700 bg-white border border-emerald-200 rounded px-1.5 py-0.5 mt-0.5">
                <ClipboardCheck className="w-2.5 h-2.5" /> Audit · CRM
              </span>
              <div className="flex-1 min-w-0 space-y-0.5">
                {b.audit_trainer_name && (
                  <p className="text-slate-600"><span className="text-slate-400">Trainer:</span> <span className="font-medium text-slate-800">{b.audit_trainer_name}</span></p>
                )}
                {b.notes_of_auditor && (
                  <p className="text-slate-600 whitespace-pre-wrap leading-relaxed" title={b.notes_of_auditor}><span className="text-slate-400">Notes:</span> {b.notes_of_auditor}</p>
                )}
              </div>
            </div>
          )}
          {attnSummary && attnSummary.sessions_held > 0 && (
            <div className="px-5 py-1.5 text-[10px] text-slate-500 bg-violet-50/40 border-b border-violet-100" data-testid={`batch-attn-summary-${b.batch_number}`}>
              Attendance <strong className="text-slate-700">{attnSummary.avg_attendance_pct != null ? `${attnSummary.avg_attendance_pct}%` : '—'}</strong> across {attnSummary.sessions_held} session{attnSummary.sessions_held === 1 ? '' : 's'}
              <span className="text-slate-400"> (window: {b.start_date} → {b.end_date || 'ongoing'})</span>
            </div>
          )}
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[9px] uppercase">Student</TableHead>
              <TableHead className="text-[9px] uppercase w-24" title="Attendance % scoped to batch start → end date">Attendance {attnLoading && <Loader2 className="w-2.5 h-2.5 animate-spin inline text-slate-400" />}</TableHead>
              <TableHead className="text-[9px] uppercase w-28">Payment</TableHead>
              <TableHead className="text-[9px] uppercase text-right w-28">Total</TableHead>
              <TableHead className="text-[9px] uppercase text-right w-24">Paid</TableHead>
              <TableHead className="text-[9px] uppercase text-right w-24">Due</TableHead>
              <TableHead className="w-6"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(b.students || []).length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 py-3">No students</TableCell></TableRow>
              ) : (b.students || []).map(s => (
                <StudentDrilldown key={s.student_id} student={s} attendance={attnByStudent?.[s.student_id]} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ─── Trainer Group (collapsible) ─────────────────────────────
function TrainerGroup({ trainer }) {
  const [expanded, setExpanded] = useState(false);
  const t = trainer;
  const totalRevenue = t.batches.reduce((sum, b) => sum + (b.batch_revenue || 0), 0);

  return (
    <Card className="border overflow-hidden" data-testid={`trainer-group-${t.trainer_name}`}>
      <div
        className="flex items-center justify-between px-5 py-3 bg-slate-50/70 cursor-pointer hover:bg-slate-100/60 select-none"
        onClick={() => setExpanded(!expanded)}
        data-testid={`trainer-expand-${t.trainer_name}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-sm">
            {(t.trainer_name || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <span className="text-sm font-semibold text-slate-800">{t.trainer_name || 'Unknown'}</span>
            <span className="ml-2 text-xs text-slate-400">({t.total_students} students)</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">{t.weekday_count} Weekday</Badge>
          <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-200">{t.weekend_count} Weekend</Badge>
          <Badge variant="outline" className="text-[9px]">{t.batch_count} batches</Badge>
          <span className="text-xs font-semibold text-slate-600 ml-1">₹{Number(totalRevenue).toLocaleString('en-IN')}</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t">
          {t.batches.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400">No batches</div>
          ) : t.batches.map(b => (
            <BatchSection key={b.batch_id} batch={b} />
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Custom Label for stacked bars ───────────────────────────
function StackLabel({ x, y, width, value }) {
  if (!value) return null;
  return (
    <text x={x + width / 2} y={y + 12} fill="#fff" textAnchor="middle" fontSize={10} fontWeight="bold">
      {value}
    </text>
  );
}

function TotalLabel({ x, y, width, value, viewBox, ...rest }) {
  // value here is the weekend value; we need the total
  return null;
}

// ─── MAIN PAGE ───────────────────────────────────────────────
export default function TrainerDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trainerId, setTrainerId] = useState('');
  const [batchType, setBatchType] = useState('');
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [category, setCategory] = useState('');
  const [progressOpen, setProgressOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const hasAnyFilter = trainerId || batchType || search || paymentFilter || category;

  const clearAllFilters = () => {
    setTrainerId('');
    setBatchType('');
    setSearch('');
    setPaymentFilter('');
    setCategory('');
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (trainerId) params.set('trainer_id', trainerId);
    if (batchType) params.set('batch_type', batchType);
    if (search) params.set('search', search);
    if (paymentFilter) params.set('payment_status', paymentFilter);
    if (category) params.set('category', category);
    api.get(`/training/trainer-dashboard?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [trainerId, batchType, search, paymentFilter, category]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary || {};
  const chartData = data?.chart_data || [];
  const tableData = data?.table_data || [];
  const trainers = (data?.trainers || []).map(t => ({ value: t.id, label: t.name }));

  // Add total to chart data for top label
  const chartWithTotal = chartData.map(d => ({ ...d, total: d.weekday + d.weekend }));

  return (
    <BookmarkMapProvider>
    <div className="space-y-5" data-testid="trainer-dashboard-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Trainer Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Student strength across trainers — Weekday vs Weekend breakdown</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setGuideOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition"
            data-testid="open-trainer-guide"
            title="How to fill PDP, Mock Interview & Video/Transcript"
          >
            <HelpCircle className="w-3 h-3" />Trainer Guide
          </button>
          <button
            onClick={() => setProgressOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition"
            data-testid="open-batch-progress"
          >
            <ListChecks className="w-3 h-3" />Batch Progress
          </button>
          <a href="/training/dpr-compliance" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition" data-testid="open-dpr-compliance">
            <Calendar className="w-3 h-3" />DPR Compliance Report
          </a>
        </div>
      </div>

      <BatchProgressModal
        open={progressOpen}
        onOpenChange={setProgressOpen}
        tableData={tableData}
      />
      <TrainerGuideModal open={guideOpen} onOpenChange={setGuideOpen} />

      {/* ─── Filters ──────────────────────────────────────── */}
      <Card className="border p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 border rounded-md overflow-hidden" data-testid="batch-type-filter">
            {[{ key: '', label: 'All' }, { key: 'weekday', label: 'Weekday' }, { key: 'weekend', label: 'Weekend' }].map(m => (
              <button
                key={m.key}
                onClick={() => setBatchType(m.key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${batchType === m.key ? 'bg-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid={`batch-type-${m.key || 'all'}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Payment status filter */}
          <div className="flex items-center gap-1 border rounded-md overflow-hidden" data-testid="payment-filter">
            {[{ key: '', label: 'All' }, { key: 'paid', label: 'Paid' }, { key: 'partially_paid', label: 'Partial' }, { key: 'overdue', label: 'Overdue' }].map(m => (
              <button
                key={m.key}
                onClick={() => setPaymentFilter(m.key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${paymentFilter === m.key ? (m.key === 'overdue' ? 'bg-red-600 text-white' : m.key === 'partially_paid' ? 'bg-amber-500 text-white' : m.key === 'paid' ? 'bg-emerald-600 text-white' : 'bg-violet-600 text-white') : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid={`payment-${m.key || 'all'}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="w-52">
            <SearchableSelect
              options={[{ value: '', label: 'All technologies' }, ...(data?.categories || []).map((c) => ({ value: c, label: c }))]}
              value={category}
              onChange={setCategory}
              placeholder="Technology..."
              testId="category-filter"
            />
          </div>

          <div className="w-52">
            <SearchableSelect
              options={trainers}
              value={trainerId}
              onChange={setTrainerId}
              placeholder="Search Trainer..."
              testId="trainer-filter"
            />
          </div>

          <div className="flex-1 min-w-[180px]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
              <Input
                placeholder="Search student, enrollment #, batch..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-9 text-xs pl-8"
                data-testid="search-input"
              />
            </div>
          </div>

          {hasAnyFilter && (
            <Button variant="outline" size="sm" className="h-9 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={clearAllFilters} data-testid="clear-filters-btn">
              <X className="w-3.5 h-3.5 mr-1" />Clear All
            </Button>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Info className="w-3.5 h-3.5" />
            <span>Total Records: <span className="font-bold text-slate-700">{summary.total_students || 0}</span></span>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
      ) : !data ? null : (
        <>
          {/* ─── Summary Cards ────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="summary-cards">
            <SummaryCard label="Trainers" value={summary.total_trainers} color="text-violet-600" icon={<Users className="w-4 h-4" />} testId="card-trainers" />
            <SummaryCard label="Total Batches" value={summary.total_batches} color="text-slate-800" icon={<BookOpen className="w-4 h-4" />} testId="card-batches" />
            <SummaryCard label="Total Students" value={summary.total_students} color="text-emerald-600" icon={<BarChart3 className="w-4 h-4" />} testId="card-students" />
            <SummaryCard label="Weekday Batches" value={summary.weekday_batches} color="text-[#4361ee]" icon={<Briefcase className="w-4 h-4" />} testId="card-weekday" subtitle={`${summary.weekday_students} students`} />
            <SummaryCard label="Weekend Batches" value={summary.weekend_batches} color="text-[#e63946]" icon={<Calendar className="w-4 h-4" />} testId="card-weekend" subtitle={`${summary.weekend_students} students`} />
          </div>

          {/* ─── Stacked Bar Chart ────────────────────────── */}
          <Card className="border p-4" data-testid="trainer-chart">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Student Strength by Trainer</p>
            </div>
            <div style={{ width: '100%', height: Math.max(350, chartWithTotal.length * 12) }}>
              <ResponsiveContainer>
                <BarChart data={chartWithTotal} margin={{ top: 20, right: 20, bottom: 80, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="trainer_name"
                    tick={{ fontSize: 9, fill: '#64748b' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'Record Count', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#94a3b8' } }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    wrapperStyle={{ paddingTop: 20, fontSize: 11 }}
                    formatter={(val) => val === 'weekday' ? 'Mon-Thursday ( Weekday )' : 'Sat-Sun ( Weekend )'}
                  />
                  <Bar dataKey="weekday" stackId="a" fill="#4361ee" radius={[0, 0, 0, 0]}>
                    <LabelList dataKey="weekday" position="center" fill="#fff" fontSize={10} fontWeight="bold" formatter={v => v || ''} />
                  </Bar>
                  <Bar dataKey="weekend" stackId="a" fill="#e63946" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="weekend" position="center" fill="#fff" fontSize={10} fontWeight="bold" formatter={v => v || ''} />
                    <LabelList dataKey="total" position="top" fill="#334155" fontSize={10} fontWeight="bold" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* ─── Grouped Table ────────────────────────────── */}
          <div className="space-y-2" data-testid="trainer-table">
            {tableData.length === 0 ? (
              <Card className="border p-12 text-center">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-xs text-slate-400">No trainer data found</p>
              </Card>
            ) : tableData.map(t => (
              <TrainerGroup key={t.trainer_name} trainer={t} />
            ))}
          </div>
        </>
      )}
    </div>
    </BookmarkMapProvider>
  );
}

function SummaryCard({ label, value, color, icon, testId, subtitle }) {
  return (
    <Card className="border p-3.5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`${color} opacity-60`}>{icon}</span>
        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      </div>
      <p className={`text-xl font-bold ${color}`}>{value ?? 0}</p>
      {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
    </Card>
  );
}
